/**
 * Fume session key schedule.
 *
 *   PRK  = HKDF-Extract(salt = R, IKM = dh)
 *   info = UTF8(label) || transcript.hash
 *   K    = HKDF-Expand(PRK, info, L = 32)
 *
 * Since one-shot HKDF-SHA256 already composes Extract-then-Expand, each derived
 * key is `hkdfSha256(ikm = dh, salt = R, info = UTF8(label) || hash, 32)`.
 *
 * The transcript canonical hash is SHA-256 over, for each message,
 * `UInt64BE(length) || bytes`.
 */
import { createHash } from 'node:crypto';
import { FumeCryptoError } from './errors.js';
import { hkdfSha256 } from './hkdf.js';
import { DH_SECRET_SIZE, HKDF_LABEL_CONTENT, HKDF_LABEL_MANIFEST, HKDF_LABEL_RECEIPT, SESSION_ROOT_SIZE, } from './constants.js';
import { bytesEqual, isAllZero } from './internal.js';
const TRANSCRIPT_HASH_SIZE = 32;
const DERIVED_KEY_SIZE = 32;
const utf8 = new TextEncoder();
/**
 * Derive a single labelled key from the session inputs.
 *
 *   info = UTF8(label) || transcriptHash
 *   K    = HKDF-Expand(HKDF-Extract(salt = R, IKM = dh), info, length)
 */
export function hkdfDerive(dh, R, transcriptHash, label, length) {
    const labelBytes = utf8.encode(label);
    const info = new Uint8Array(labelBytes.length + transcriptHash.length);
    info.set(labelBytes, 0);
    info.set(transcriptHash, labelBytes.length);
    // salt = R, IKM = dh  (per the spec's HKDF-Extract(salt = R, IKM = dh)).
    return hkdfSha256(dh, R, info, length);
}
/**
 * Derive the content/manifest/receipt keys from the session root `R`, the
 * PAKE/DH shared secret `dh`, and the SAS-verified transcript.
 *
 * Enforces (throwing {@link FumeCryptoError} on any violation):
 *   - `R.length === 32`
 *   - `dh.length === 32` and `dh` is not the all-zero vector
 *   - `transcript.hash.length === 32`
 *   - `transcript.sasVerified === true`
 */
export function deriveKeys(R, dh, transcript) {
    if (!(R instanceof Uint8Array) || R.length !== SESSION_ROOT_SIZE) {
        throw new FumeCryptoError(`R must be exactly ${SESSION_ROOT_SIZE} bytes`);
    }
    if (!(dh instanceof Uint8Array) || dh.length !== DH_SECRET_SIZE) {
        throw new FumeCryptoError(`dh must be exactly ${DH_SECRET_SIZE} bytes`);
    }
    if (isAllZero(dh)) {
        throw new FumeCryptoError('dh must not be the all-zero vector');
    }
    // Invariant I1 (S2 hardening): the PAKE/DH secret must be a genuine shared
    // secret, never the session root itself. `R` as `dh` is a wrong-secret /
    // misuse signal and is rejected outright rather than silently deriving.
    if (bytesEqual(dh, R)) {
        throw new FumeCryptoError('dh must not equal the session root R');
    }
    if (transcript == null ||
        !(transcript.hash instanceof Uint8Array) ||
        transcript.hash.length !== TRANSCRIPT_HASH_SIZE) {
        throw new FumeCryptoError(`transcript.hash must be exactly ${TRANSCRIPT_HASH_SIZE} bytes`);
    }
    // Invariant I1 (S2 hardening): an all-zero transcript hash means the transcript
    // was never bound (pre-auth); reject so keys can only follow a real transcript.
    if (isAllZero(transcript.hash)) {
        throw new FumeCryptoError('transcript.hash must not be the all-zero vector (transcript unbound)');
    }
    if (transcript.sasVerified !== true) {
        throw new FumeCryptoError('transcript.sasVerified must be true (SAS not verified)');
    }
    return {
        contentKey: hkdfDerive(dh, R, transcript.hash, HKDF_LABEL_CONTENT, DERIVED_KEY_SIZE),
        manifestKey: hkdfDerive(dh, R, transcript.hash, HKDF_LABEL_MANIFEST, DERIVED_KEY_SIZE),
        receiptKey: hkdfDerive(dh, R, transcript.hash, HKDF_LABEL_RECEIPT, DERIVED_KEY_SIZE),
    };
}
/**
 * Canonical transcript hash: SHA-256 over the concatenation of, for each
 * message, `UInt64BE(length) || messageBytes`.
 */
export function hashTranscript(messages) {
    if (!Array.isArray(messages)) {
        throw new FumeCryptoError('messages must be an array of Uint8Array');
    }
    const hash = createHash('sha256');
    const lengthPrefix = Buffer.alloc(8);
    for (const message of messages) {
        if (!(message instanceof Uint8Array)) {
            throw new FumeCryptoError('every transcript message must be a Uint8Array');
        }
        lengthPrefix.writeBigUInt64BE(BigInt(message.length));
        hash.update(lengthPrefix);
        hash.update(message);
    }
    return new Uint8Array(hash.digest());
}
//# sourceMappingURL=key-schedule.js.map